import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Authentticatevalues, ROUTES, STORAGE_KEYS } from '@/constants';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import { getLastTwoUserLogEvents } from '@/service/userLogService';
import { useAppSelector, selectUser, selectSessionId } from '@/store';

import type { PlantInfo } from '@/types';

/**
 * Matches Xamarin RedirectToSpecificPage — checks user's last 2 activity logs
 * after plant selection and navigates to the appropriate page.
 */
export function useRedirectAfterPlantSelection() {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const sessionId = useAppSelector(selectSessionId);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const redirectAfterPlantSelect = useCallback(
    async (plant: PlantInfo) => {
      setIsRedirecting(true);
      const userId = user?._id ?? '';

      // Always log login after plant selection — matches Xamarin
      await UpdateUserLogAsync({
        user: userId,
        status: Authentticatevalues.UserStatusLogin,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant._id,
      }).catch(() => {
        // Fire-and-forget
      });

      try {
        const response = await getLastTwoUserLogEvents(String(plant.org_id), userId);

        const events = Array.isArray(response.Events) ? response.Events : [];

        if (events.length === 2) {
          const lastActivity = events[0]?.UserAction ?? '';
          const previousActivity = events[1]?.UserAction ?? '';

          // Case 1: break + stop_yard_work → Break Mode (from yard work)
          if (
            lastActivity === Authentticatevalues.UserStatusBreak &&
            previousActivity === Authentticatevalues.UserStatusEndYardWork
          ) {
            navigate(ROUTES.breakMode, { replace: true });
            return;
          }

          // Case 2: start_shuttle_transfer + stop_yard_work → Shuttle Transfer
          if (
            lastActivity === Authentticatevalues.UserStatusStartShuttleTransfer &&
            previousActivity === Authentticatevalues.UserStatusEndYardWork
          ) {
            localStorage.setItem(STORAGE_KEYS.shuttleModeActive, 'true');
            navigate(ROUTES.shuttleTransfer, { replace: true });
            return;
          }

          // Case 3: continue + break → Shuttle Transfer (resume from break during shuttle)
          if (
            lastActivity === Authentticatevalues.UserStatusContinue &&
            previousActivity === Authentticatevalues.UserStatusBreak
          ) {
            localStorage.setItem(STORAGE_KEYS.shuttleModeActive, 'true');
            navigate(ROUTES.shuttleTransfer, { replace: true });
            return;
          }

          // Case 4: break + stop_shuttle_transfer → Break Mode from shuttle
          if (
            lastActivity === Authentticatevalues.UserStatusBreak &&
            previousActivity === Authentticatevalues.UserStatusStopShuttleTransfer
          ) {
            navigate(`${ROUTES.breakMode}?from=shuttle`, { replace: true });
            return;
          }
        }

        navigate(ROUTES.workModeSelection, { replace: true });
      } catch {
        // API failure — graceful fallback to Work Mode Selection
        navigate(ROUTES.workModeSelection, { replace: true });
      } finally {
        setIsRedirecting(false);
      }
    },
    [navigate, user?._id, sessionId]
  );

  return { redirectAfterPlantSelect, isRedirecting };
}
